import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Box,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    Chip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Preview as PreviewIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { marked } from 'marked';

// Компонент для отображения markdown превью
const MarkdownPreview = ({ content, darkMode }) => {
    if (!content) return null;
    // if (!content) return <Typography variant="body2" color="text.secondary">Нет текста для превью</Typography>;
    
    const htmlContent = marked(content);
    
    return (
        <Box
            sx={{
                maxHeight: '150px',
                overflow: 'auto',
                padding: '12px',
                borderRadius: 2,
                backgroundColor: darkMode ? 'rgba(33, 150, 243, 0.05)' : 'rgba(21, 101, 192, 0.05)',
                color: darkMode ? '#2196f3' : '#1565c0',
                fontWeight: 500,
                lineHeight: '1.4',
                border: darkMode 
                    ? '1px solid rgba(33, 150, 243, 0.2)' 
                    : '1px solid rgba(21, 101, 192, 0.2)',
                '& p': {
                    margin: '4px 0',
                    fontSize: '0.875rem',
                    lineHeight: '1.4',
                    color: darkMode ? '#2196f3' : '#1565c0',
                },
                '& strong': {
                    fontWeight: 'bold',
                    color: darkMode ? '#2196f3' : '#1565c0',
                },
                '& em': {
                    fontStyle: 'italic',
                    color: darkMode ? '#2196f3' : '#1565c0',
                },
                '& ul, & ol': {
                    margin: '4px 0',
                    paddingLeft: '16px',
                },
                '& li': {
                    margin: '2px 0',
                    fontSize: '0.875rem',
                    color: darkMode ? '#2196f3' : '#1565c0',
                },
                '& code': {
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    color: darkMode ? '#fff' : '#000',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                },
                '& pre': {
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    color: darkMode ? '#fff' : '#000',
                    padding: '8px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '0.8rem',
                },
                '& blockquote': {
                    borderLeft: `3px solid ${darkMode ? '#2196f3' : '#1565c0'}`,
                    paddingLeft: '12px',
                    margin: '4px 0',
                    fontStyle: 'italic',
                    color: darkMode ? '#2196f3' : '#1565c0',
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: '4px 0',
                    fontWeight: 'bold',
                    color: darkMode ? '#2196f3' : '#1565c0',
                },
                '& h1': { fontSize: '1.1rem' },
                '& h2': { fontSize: '1rem' },
                '& h3': { fontSize: '0.95rem' },
                '& h4': { fontSize: '0.9rem' },
                '& h5': { fontSize: '0.85rem' },
                '& h6': { fontSize: '0.8rem' },
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
    );
};

/**
 * Универсальный компонент для редактирования полей с оснащением и текстом.
 * Может использоваться для редактирования комментариев, описаний и других полей,
 * которые имеют структуру: оснащение (equipment) + текст (comments).
 * 
 * @param {boolean} open - Открыт ли диалог
 * @param {function} onClose - Функция закрытия диалога
 * @param {function} onSave - Функция сохранения, принимает массив текстов
 * @param {Array} equipment - Массив элементов оснащения
 * @param {Array} comments - Массив текстов для каждого элемента оснащения
 * @param {boolean} darkMode - Режим темной темы
 * @param {string} objectName - Название объекта (отображается в заголовке)
 * @param {string} title - Заголовок диалога
 * @param {string} equipmentLabel - Название колонки оснащения (по умолчанию "Оснащение")
 * @param {string} textLabel - Название колонки текста (по умолчанию "Комментарий")
 * @param {string} previewLabel - Название колонки превью (по умолчанию "Превью")
 * @param {string} placeholder - Placeholder для поля ввода текста
 * @param {string} generalItemLabel - Название общего элемента, когда нет оснащения
 */
const EquipmentTextEditDialog = ({ 
    open, 
    onClose, 
    onSave, 
    equipment, 
    comments, 
    darkMode,
    objectName = 'Объект',
    title = 'Редактирование комментариев',
    equipmentLabel = 'Оснащение',
    textLabel = 'Комментарий',
    previewLabel = 'Превью',
    placeholder = 'Введите комментарий (поддерживается Markdown: **жирный**, *курсив*, # заголовок, - список)',
    generalItemLabel = 'Общий комментарий'
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Локальное состояние для комментариев
    const [localComments, setLocalComments] = useState([]);
    
    // Инициализация локального состояния при открытии диалога
    useEffect(() => {
        if (open) {
            // Создаем массив комментариев для каждого элемента оснащения
            const commentsArray = Array.isArray(comments) ? comments : [];
            const equipmentArray = Array.isArray(equipment) ? equipment : [];
            
            // Если нет оснащения, создаем одну строку для общего комментария
            if (equipmentArray.length === 0) {
                setLocalComments(commentsArray.length > 0 ? commentsArray : ['']);
            } else {
                // Убеждаемся, что у нас есть комментарий для каждого элемента оснащения
                const initializedComments = equipmentArray.map((_, index) => 
                    commentsArray[index] || ''
                );
                setLocalComments(initializedComments);
            }
        }
    }, [open, equipment, comments]);
    
    // Обработчик изменения комментария
    const handleCommentChange = (index, value) => {
        const newComments = [...localComments];
        newComments[index] = value;
        setLocalComments(newComments);
    };
    
    // Обработчик сохранения
    const handleSave = () => {
        onSave(localComments);
        onClose();
    };
    
    // Обработчик отмены
    const handleCancel = () => {
        setLocalComments([]);
        onClose();
    };
    
    // Определяем данные для отображения
    const displayData = Array.isArray(equipment) && equipment.length > 0 
        ? equipment 
        : [{ name: generalItemLabel, isGeneral: true }];
    
    if (!open) {
        return null;
    }
    
    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            sx={{ zIndex: 5100 }}
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                        color: darkMode ? '#f5f7fa' : '#333',
                        borderRadius: isMobile ? 0 : 3,
                        boxShadow: darkMode
                            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                            : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        animation: 'slideIn 0.3s ease-out',
                        position: 'relative',
                        zIndex: 1301,
                        '@keyframes slideIn': {
                            from: {
                                opacity: 0,
                                transform: 'scale(0.95)',
                            },
                            to: {
                                opacity: 1,
                                transform: 'scale(1)',
                            },
                        },
                    }
                }
            }}
        >
            <DialogTitle sx={{
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                py: 1,
                px: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: darkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(21, 101, 192, 0.1)',
                        border: `2px solid ${darkMode ? '#2196f3' : '#1565c0'}`,
                    }}>
                        <EditIcon sx={{ 
                            color: darkMode ? '#2196f3' : '#1565c0',
                            fontSize: '20px'
                        }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ 
                            color: darkMode ? '#f5f7fa' : '#333',
                            fontWeight: 600,
                            mb: 0.5
                        }}>
                            {title}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                            color: darkMode ? '#bbb' : '#666',
                            fontSize: '0.875rem'
                        }}>
                            {objectName}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={handleCancel}
                    size="small"
                    sx={{ 
                        color: darkMode ? '#bbb' : '#666',
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
                            color: darkMode ? '#f5f7fa' : '#333'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{
                bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                p: 3,
                position: 'relative',
                zIndex: 1302
            }}>
                <TableContainer component={Paper} sx={{
                    backgroundColor: darkMode ? '#2b2b2b' : '#fff',
                    borderRadius: 2,
                    boxShadow: darkMode 
                        ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
                        : '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: darkMode 
                        ? '1px solid rgba(255, 255, 255, 0.12)' 
                        : '1px solid rgba(0, 0, 0, 0.12)',
                }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{
                                backgroundColor: darkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(21, 101, 192, 0.1)',
                            }}>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: darkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(21, 101, 192, 0.1)',
                                    color: darkMode ? '#2196f3' : '#1565c0',
                                    borderBottom: darkMode 
                                        ? '1px solid rgba(255, 255, 255, 0.12)' 
                                        : '1px solid rgba(0, 0, 0, 0.12)',
                                    padding: '16px',
                                    fontSize: '0.875rem',
                                }}>
                                    {equipmentLabel}
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: darkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(21, 101, 192, 0.1)',
                                    color: darkMode ? '#2196f3' : '#1565c0',
                                    borderBottom: darkMode 
                                        ? '1px solid rgba(255, 255, 255, 0.12)' 
                                        : '1px solid rgba(0, 0, 0, 0.12)',
                                    padding: '16px',
                                    fontSize: '0.875rem',
                                }}>
                                    {textLabel}
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: darkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(21, 101, 192, 0.1)',
                                    color: darkMode ? '#2196f3' : '#1565c0',
                                    borderBottom: darkMode 
                                        ? '1px solid rgba(255, 255, 255, 0.12)' 
                                        : '1px solid rgba(0, 0, 0, 0.12)',
                                    padding: '16px',
                                    fontSize: '0.875rem',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PreviewIcon sx={{ fontSize: '18px', color: darkMode ? '#2196f3' : '#1565c0' }} />
                                        {previewLabel}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayData.map((equipmentItem, index) => (
                                <TableRow key={index} sx={{
                                    '&:hover': {
                                        backgroundColor: darkMode ? 'rgba(33, 150, 243, 0.05)' : 'rgba(21, 101, 192, 0.05)',
                                    },
                                }}>
                                    <TableCell sx={{
                                        borderBottom: darkMode 
                                            ? '1px solid rgba(255, 255, 255, 0.12)' 
                                            : '1px solid rgba(0, 0, 0, 0.12)',
                                        borderRight: darkMode 
                                            ? '1px solid rgba(255, 255, 255, 0.08)' 
                                            : '1px solid rgba(0, 0, 0, 0.08)',
                                        minWidth: '200px',
                                        padding: '16px',
                                        verticalAlign: 'top',
                                    }}>
                                        <Chip
                                            label={equipmentItem?.name || equipmentItem || `Элемент ${index + 1}`}
                                            size="small"
                                            sx={{
                                                bgcolor: equipmentItem?.isGeneral 
                                                    ? (darkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(21, 101, 192, 0.1)')
                                                    : (darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)'),
                                                color: equipmentItem?.isGeneral 
                                                    ? (darkMode ? '#2196f3' : '#1565c0')
                                                    : (darkMode ? '#7486F0' : '#4154C4'),
                                                fontSize: '0.8rem',
                                                height: 'auto',
                                                minHeight: '28px',
                                                maxWidth: '100%',
                                                fontWeight: 500,
                                                '& .MuiChip-label': {
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    textAlign: 'center',
                                                    lineHeight: '1.2',
                                                    padding: '4px 8px',
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        borderBottom: darkMode 
                                            ? '1px solid rgba(255, 255, 255, 0.12)' 
                                            : '1px solid rgba(0, 0, 0, 0.12)',
                                        borderRight: darkMode 
                                            ? '1px solid rgba(255, 255, 255, 0.08)' 
                                            : '1px solid rgba(0, 0, 0, 0.08)',
                                        minWidth: '350px',
                                        padding: '16px',
                                        verticalAlign: 'top',
                                    }}>
                                        <TextField
                                            multiline
                                            rows={4}
                                            fullWidth
                                            value={localComments[index] || ''}
                                            onChange={(e) => handleCommentChange(index, e.target.value)}
                                            placeholder={placeholder}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: darkMode ? '#1e1e1e' : '#fff',
                                                    borderRadius: 2,
                                                    '& fieldset': {
                                                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                                        borderWidth: '1px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: darkMode ? '#2196f3' : '#1565c0',
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    fontSize: '0.875rem',
                                                    padding: '12px',
                                                    lineHeight: '1.5',
                                                },
                                                '& .MuiInputBase-input::placeholder': {
                                                    color: darkMode ? '#999' : '#666',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        borderBottom: darkMode 
                                            ? '1px solid rgba(255, 255, 255, 0.12)' 
                                            : '1px solid rgba(0, 0, 0, 0.12)',
                                        minWidth: '300px',
                                        padding: '16px',
                                        verticalAlign: 'top',
                                    }}>
                                        <MarkdownPreview 
                                            content={localComments[index] || ''} 
                                            darkMode={darkMode} 
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            
            <DialogActions sx={{
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                borderTop: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                py: 2,
                px: 3,
                gap: 2,
            }}>
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    sx={{
                        color: darkMode ? '#bbb' : '#666',
                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                        px: 3,
                        py: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                            backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
                            color: darkMode ? '#f5f7fa' : '#333',
                        },
                    }}
                >
                    Отмена
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{
                        backgroundColor: darkMode ? '#2196f3' : '#1565c0',
                        color: '#fff',
                        px: 3,
                        py: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        borderRadius: 2,
                        textTransform: 'none',
                        boxShadow: darkMode 
                            ? '0 4px 12px rgba(33, 150, 243, 0.3)' 
                            : '0 4px 12px rgba(21, 101, 192, 0.3)',
                        '&:hover': {
                            backgroundColor: darkMode ? '#1976d2' : '#0d47a1',
                            boxShadow: darkMode 
                                ? '0 6px 16px rgba(33, 150, 243, 0.4)' 
                                : '0 6px 16px rgba(21, 101, 192, 0.4)',
                        },
                    }}
                >
                    Сохранить
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EquipmentTextEditDialog;

